import { useWorkspaceContext } from "../../../shared/hooks/useWorkspaceContext";
import { useState } from "react";
import { Button, Card, Combobox, Input, Modal, ModalFooter } from "@insights/ui";
import { useParams } from "react-router";
import { Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import {
  useDecisionInbox,
  useCollaboration,
  useRecordEvidenceOpen,
  useUpdateFinding,
  type DecisionInboxFilters,
} from "../hooks/useDecisionInbox";
import { FindingCard } from "../components/FindingCard";
import { TeamInteractions } from "../components/TeamInteractions";
import "../styles/decision-inbox.css";

const defaults: DecisionInboxFilters = {
  repository: "",
  teamId: "",
  state: "active",
  kind: "",
};

export function DecisionInboxPage() {
  const { workspaceId } = useParams();
  const workspace = useWorkspaceContext(workspaceId);
  const [filters, setFilters] = useState(defaults);
  const inbox = useDecisionInbox(workspaceId, filters);
  const collaboration = useCollaboration(workspaceId, {
    repository: filters.repository,
    teamId: filters.teamId,
  });
  const updateFinding = useUpdateFinding(workspaceId);
  const evidenceOpen = useRecordEvidenceOpen(workspaceId);
  const [dismissFindingId, setDismissFindingId] = useState<string | null>(null);
  const [dismissalReason, setDismissalReason] = useState("");
  const findings = inbox.data?.findings ?? [];

  return (
    <AppShell>
      <PageHeader
        eyebrow="Pulse"
        title="Inbox"
        description="Prioritized engineering risks and opportunities, with the evidence behind each finding."
      />
      <div className="finding-filters" aria-label="Inbox filters">
        <Combobox
          label="Repository"
          value={filters.repository || "all"}
          options={[
            { value: "all", label: "All selected repositories" },
            ...(inbox.data?.scopeOptions.repositories ?? []).map((repository) => ({
              value: repository,
              label: repository,
            })),
          ]}
          onValueChange={(value) => setFilters((current) => ({
            ...current,
            repository: value === "all" ? "" : value,
          }))}
        />
        <Combobox
          label="Team"
          value={filters.teamId || "all"}
          options={[
            { value: "all", label: "All teams" },
            ...(inbox.data?.scopeOptions.teams ?? []).map((team) => ({
              value: team.id,
              label: team.name,
            })),
          ]}
          onValueChange={(value) => setFilters((current) => ({
            ...current,
            teamId: value === "all" ? "" : value,
          }))}
        />
        <Combobox
          label="Finding type"
          value={filters.kind || "all"}
          options={[
            { value: "all", label: "All finding types" },
            ...(workspace.data?.features.provenance ? [{ value: "provenance-gap", label: "Provenance gaps" }] : []),
            { value: "high-churn", label: "High churn" },
            { value: "maintenance-risk", label: "Maintenance risk" },
            { value: "ownership-concentration", label: "Knowledge concentration" },
            { value: "review-bottleneck", label: "Review bottleneck" },
            { value: "removal-candidate", label: "Removal investigation" },
          ]}
          onValueChange={(value) => setFilters((current) => ({
            ...current,
            kind: value === "all" ? "" : value as NonNullable<DecisionInboxFilters["kind"]>,
          }))}
        />
        <Combobox
          label="Status"
          value={filters.state}
          options={[
            { value: "active", label: "Needs attention" },
            { value: "acknowledged", label: "Acknowledged" },
            { value: "resolved", label: "Resolved" },
            { value: "dismissed", label: "Dismissed" },
            { value: "all", label: "All findings" },
          ]}
          onValueChange={(value) => setFilters((current) => ({
            ...current,
            state: value as DecisionInboxFilters["state"],
          }))}
        />
      </div>

      {collaboration.isLoading && <Loading />}
      {collaboration.isError && (
        <Card className="finding-empty" role="alert">
          Unable to load team interactions.
        </Card>
      )}
      {collaboration.data && <TeamInteractions data={collaboration.data} />}

      {inbox.isLoading && <Loading />}
      {inbox.isError && <Card className="finding-empty" role="alert">Unable to load engineering findings.</Card>}
      {inbox.data && (
        <>
          <p className="finding-coverage">
            {inbox.data.coverageNote}
            {inbox.data.lastComputedAt
              ? ` Last computed ${new Date(inbox.data.lastComputedAt).toLocaleString()}.`
              : " The initial computation will run in the next daily background cycle."}
          </p>
          {!findings.length && (
            <Card className="finding-empty">
              No findings match this scope. This may mean no risks crossed the evidence thresholds or data collection is still incomplete.
            </Card>
          )}
          <div className="finding-list">
            {findings.map((finding, index) => (
              <FindingCard
                busy={updateFinding.isPending}
                finding={finding}
                key={finding.id}
                rank={index + 1}
                onAcknowledge={() => updateFinding.mutate({
                  findingId: finding.id,
                  state: "acknowledged",
                })}
                onDismiss={() => {
                  setDismissFindingId(finding.id);
                  setDismissalReason("");
                }}
                onEvidenceOpen={() => evidenceOpen.mutate(finding.id)}
                onResolve={() => updateFinding.mutate({
                  findingId: finding.id,
                  state: "resolved",
                })}
              />
            ))}
          </div>
        </>
      )}
      <Modal
        open={dismissFindingId !== null}
        onOpenChange={(open) => {
          if (!open) setDismissFindingId(null);
        }}
        title="Dismiss finding"
        description="Explain why this finding is not useful so repeated false positives can be investigated."
      >
        <label className="finding-dismissal-field">
          <span>Reason</span>
          <Input
            autoFocus
            maxLength={500}
            onChange={(event) => setDismissalReason(event.target.value)}
            placeholder="For example: generated code owned by another system"
            value={dismissalReason}
          />
        </label>
        <ModalFooter>
          <Button
            color="neutral"
            onClick={() => setDismissFindingId(null)}
          >
            Cancel
          </Button>
          <Button
            disabled={!dismissalReason.trim() || updateFinding.isPending}
            onClick={() => {
              if (!dismissFindingId || !dismissalReason.trim()) return;
              updateFinding.mutate({
                findingId: dismissFindingId,
                state: "dismissed",
                dismissalReason: dismissalReason.trim(),
              }, {
                onSuccess: () => setDismissFindingId(null),
              });
            }}
          >
            Dismiss finding
          </Button>
        </ModalFooter>
      </Modal>
    </AppShell>
  );
}
